import { useState, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { selectIsOffline, selectAgentId } from '~/agent/store/agent.selectors'
import { CustomerSegment } from '~/basket/model/common/segment.model'
import { selectContractId } from '~/checkout/store/checkout.selectors'
import { useAppDepsContext } from '~/core/hooks/useAppDepsContext'
import useCustomerSummary from '~/customer/components/CustomerSummary/useCustomerSummary'
import { selectIsCartera } from '~/sale/store/sale.selectors'
import { selectTheme } from '~/ui/theme/store/theme.selectors'

interface useSalesConsentsReturn {
  showConsents: boolean
  consentsIframeSrc: string
}

export default function useSalesConsents(): useSalesConsentsReturn {
  const { consentsUrl } = useAppDepsContext().runtimeConfig
  const { docId, segment } = useCustomerSummary()
  const isCartera = useSelector(selectIsCartera)
  const isOffline = useSelector(selectIsOffline)
  const userId = useSelector(selectAgentId)
  const brand = useSelector(selectTheme)
  const contractId = useSelector(selectContractId)
  const [token, setToken] = useState('')
  const [showConsents, setShowConsents] = useState(true)
  const consentType = isCartera ? 'campaign' : 'acquisition'
  const channel = isOffline ? 'webdealer' : 'myventa'

  const segmentForConsents = useMemo(() => {
    if (segment === CustomerSegment.RESIDENTIAL) return 'residential'
    if (segment === CustomerSegment.FREELANCE) return 'selfEmployed'
    if (segment === CustomerSegment.COMPANY) setShowConsents(false)
    return ''
  }, [segment])

  const consentsIframeSrc = useMemo(
    () =>
      `${consentsUrl}/?brand=${brand}&consentType=${consentType}&segment=${segmentForConsents}&channel=${channel}&sfid=${userId}&contractAgreementId=${
        contractId || ''
      }&verbalAgreementId=' '&customerDocumentNumber=${docId}&lang=es-ES&token=${token}`,
    [brand, consentType, segment, channel, userId, contractId, docId, token, consentsUrl],
  )

  function getTokenFromIndexedDb() {
    const request = window.indexedDB.open('AUTH_DB', 1)
    request.onsuccess = () => {
      const db = request.result
      const transaction = db.transaction('auth', 'readonly')
      const objectStore = transaction.objectStore('auth')
      const req = objectStore.openCursor()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      req.onsuccess = (e: any) => {
        setToken(e?.target?.result?.value?.accessToken)
      }
    }
  }

  useEffect(() => {
    getTokenFromIndexedDb()
  }, [])

  useEffect(() => {
    if (!token || segment === CustomerSegment.COMPANY) return setShowConsents(false)
    return setShowConsents(true)
  }, [token])

  return {
    showConsents,
    consentsIframeSrc,
  }
}
